import { Component, Input, OnInit, OnDestroy } from "@angular/core";
import { ContactList } from "../../../../models/contact-list.model";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Subject, takeUntil, debounceTime } from "rxjs";
import { HttpClient, HttpParams } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { GetClientsResponseDto, GetClientsDto } from "src/app/pages/call-center/pages/clients/dto/clients.dto";
import { FeiraClienteService } from "src/app/pages/call-center/pages/clients/services/feira-cliente.service";
import { FeiraClienteDto } from "src/app/pages/call-center/pages/clients/dto/feira-cliente.dto";
import { ContactService } from "../../../../contact.service";
import { ToastService } from "src/app/shared/toasts/toast-service";

@Component({
  selector: "app-add-contact-modal",
  templateUrl: "./add-contact-modal.component.html",
  styleUrl: "./add-contact-modal.component.scss",
})
export class AddContactModalComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  @Input() list!: ContactList;

  addContactForm!: FormGroup;
  isLoading = false;
  salvando = false;

  // Cliente
  buscaCliente = '';
  buscaClienteDebounce$ = new Subject<string>();
  clientesFiltrados: GetClientsDto[] = [];
  clienteSelecionado: GetClientsDto | null = null;
  dropdownClienteAberto = false;
  clientePaginas = {
    paginaAtual: 1,
    totalPaginas: 1,
    carregandoMais: false,
    temMais: true
  };

  // Feira
  buscaFeira = '';
  buscaFeiraDebounce$ = new Subject<string>();
  feirasFiltradas: FeiraClienteDto[] = [];
  feiraSelecionada: FeiraClienteDto | null = null;
  dropdownFeiraAberto = false;
  private todasFeiras: FeiraClienteDto[] = [];

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private http: HttpClient,
    private feiraClienteService: FeiraClienteService,
    private contactService: ContactService,
    private toastService: ToastService
  ) {
    this.addContactForm = this.fb.group({
      clienteId: ['', Validators.required],
      feiraId: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    // Debounce para busca de cliente
    this.buscaClienteDebounce$
      .pipe(
        debounceTime(300),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.clientePaginas.paginaAtual = 1;
        this.carregarClientes(true);
      });

    // Debounce para busca de feira
    this.buscaFeiraDebounce$
      .pipe(
        debounceTime(300),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.filtrarFeirasLocalmente();
      });

    // Observar mudanças no clienteId para carregar feiras
    this.addContactForm.get('clienteId')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((clienteId) => {
        if (clienteId) {
          this.carregarFeiras(true);
        } else {
          this.feirasFiltradas = [];
          this.todasFeiras = [];
          this.feiraSelecionada = null;
          this.buscaFeira = '';
          this.addContactForm.patchValue({ feiraId: '' }, { emitEvent: false });
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  carregarClientes(resetar: boolean = true): void {
    if (resetar) {
      this.clientePaginas = {
        paginaAtual: 1,
        totalPaginas: 1,
        carregandoMais: false,
        temMais: true
      };
      this.clientesFiltrados = [];
    }

    if (this.clientePaginas.carregandoMais || !this.clientePaginas.temMais) {
      return;
    }

    const termo = this.buscaCliente || '';
    let params = new HttpParams()
      .set('Paginacao.PaginaAtual', this.clientePaginas.paginaAtual.toString())
      .set('Paginacao.QuantidadePorPagina', '10')
      .set('OrdemAscendente', 'true');
    
    // Adicionar filtro por nome apenas se houver termo de busca
    if (termo) {
      params = params.set('Nome', termo);
    }

    if (resetar) {
      this.clientePaginas.carregandoMais = true;
    }

    this.http.get<GetClientsResponseDto>(`${environment.apiUrl}/v1/clientes`, { params })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const novosClientes = response.clientes || [];

          if (resetar) {
            this.clientesFiltrados = novosClientes;
          } else {
            this.clientesFiltrados = [...this.clientesFiltrados, ...novosClientes];
          }

          const totalPaginas = Math.ceil((response.paginacao?.totalDeItens || 0) / 10);
          this.clientePaginas.totalPaginas = totalPaginas || 1;
          this.clientePaginas.temMais = this.clientePaginas.paginaAtual < this.clientePaginas.totalPaginas;
          this.clientePaginas.carregandoMais = false;

          // Se houver poucos clientes e ainda tiver mais páginas, carregar automaticamente
          if (resetar && this.clientesFiltrados.length < 10 && this.clientePaginas.temMais) {
            setTimeout(() => {
              this.carregarMaisClientes();
            }, 100);
          }
        },
        error: (error) => {
          console.error('Erro ao carregar clientes:', error);
          this.toastService.show('Erro ao carregar clientes', {
            classname: 'bg-danger text-light',
          });
          this.clientePaginas.carregandoMais = false;
        }
      });
  }

  carregarMaisClientes(): void {
    if (this.clientePaginas.carregandoMais || !this.clientePaginas.temMais) {
      return;
    }
    this.clientePaginas.paginaAtual++;
    this.carregarClientes(false);
  }

  onScrollClientes(event: Event): void {
    const element = event.target as HTMLElement;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;

    if (scrollTop + clientHeight >= scrollHeight * 0.9) {
      this.carregarMaisClientes();
    }
  }

  abrirDropdownCliente(): void {
    this.dropdownClienteAberto = true;
    // Sempre recarregar quando abrir o dropdown para garantir dados atualizados
    if (this.clientesFiltrados.length === 0 || this.buscaCliente === '') {
      this.carregarClientes(true);
    }
  }

  fecharDropdownCliente(): void {
    setTimeout(() => {
      this.dropdownClienteAberto = false;
    }, 200);
  }

  selecionarCliente(cliente: GetClientsDto): void {
    this.clienteSelecionado = cliente;
    this.buscaCliente = cliente.nome;
    this.addContactForm.patchValue({ clienteId: cliente.id });
    this.fecharDropdownCliente();
  }

  carregarFeiras(resetar: boolean = true): void {
    const clienteId = this.addContactForm.get('clienteId')?.value;
    if (!clienteId) {
      return;
    }

    if (resetar) {
      this.feirasFiltradas = [];
      this.todasFeiras = [];
      this.feiraSelecionada = null;
      this.buscaFeira = '';
    }

    this.isLoading = true;

    this.feiraClienteService.getFeirasCliente(clienteId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (feiras) => {
          this.todasFeiras = feiras || [];
          this.filtrarFeirasLocalmente();
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Erro ao carregar feiras:', error);
          this.toastService.show('Erro ao carregar feiras', {
            classname: 'bg-danger text-light',
          });
          this.isLoading = false;
        }
      });
  }

  filtrarFeirasLocalmente(): void {
    if (!this.buscaFeira) {
      this.feirasFiltradas = this.todasFeiras;
      return;
    }

    const termo = this.buscaFeira.toLowerCase();
    this.feirasFiltradas = this.todasFeiras.filter(feira =>
      feira.nome?.toLowerCase().includes(termo)
    );
  }

  abrirDropdownFeira(): void {
    if (!this.addContactForm.get('clienteId')?.value) {
      return;
    }
    this.dropdownFeiraAberto = true;
    if (this.todasFeiras.length === 0) {
      this.carregarFeiras(true);
    }
  }

  fecharDropdownFeira(): void {
    setTimeout(() => {
      this.dropdownFeiraAberto = false;
    }, 200);
  }

  selecionarFeira(feira: FeiraClienteDto): void {
    this.feiraSelecionada = feira;
    this.buscaFeira = feira.nome || '';
    this.addContactForm.patchValue({ feiraId: feira.feiraId || feira.id });
    this.fecharDropdownFeira();
  }

  onSubmit(): void {
    if (this.addContactForm.invalid) {
      this.addContactForm.markAllAsTouched();
      return;
    }

    this.salvando = true;

    const formValue = this.addContactForm.value;
    const payload = {
      clienteId: formValue.clienteId,
      feiraId: formValue.feiraId,
      listaDeContatosGeradosId: this.list.id
    };

    this.contactService.incluirContatoEmLista(payload).subscribe({
      next: () => {
        this.toastService.show('Contato adicionado à lista com sucesso', {
          classname: 'bg-success text-light',
        });
        this.activeModal.close(true);
      },
      error: (error) => {
        console.error('Erro ao adicionar contato:', error);
        const mensagem = error?.error?.errors?.Mensagens?.[0] || 'Erro ao adicionar contato à lista';
        this.toastService.show(mensagem, {
          classname: 'bg-danger text-light',
        });
        this.salvando = false;
      }
    });
  }

  cancelar(): void {
    this.activeModal.dismiss();
  }
}

